<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
} from 'vue'
import CUSTOM_FIELD_TYPES from '@/constants/customFieldTypes'
import InputHearingFieldItem from '@/components/hearingField/InputHearingFieldItem.vue'
import TextareaForm from '@/components/form/TextareaForm.vue'
import hearingFieldsUtil from '@/models/hearingFieldsUtil'
import bindData from '@/utilities/bindData'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'InputHearingFieldItems',

  components: {
    InputHearingFieldItem,
    TextareaForm,
  },

  props: {
    /**
     * バリデーションの名前空間
     * @description 自身に紐づく名前空間のバリデーションコールにのみ反応する。
     * @description 値は任意であるが、store に登録されるバリデーションキューの namespace の値と同一にする必要がある。
     * @description Form submit の場合、form の name 属性の値にする。
     */
    namespace: {
      type: String,
      default: '',
    },

    hearingFieldConfig: {
      type: Object as PropType<HearingFieldConfig>,
      required: true,
    },

    inputHearingFields: {
      type: Object as PropType<Inputs>,
      required: true,
    },

    isShowSectionTitle: {
      type: Boolean,
      default: true,
    }
  },

  emits: [
    'update:hearingFields',
    'select:primary',
    'close:secondary',
  ],

  setup(props, { emit }) {
    const {
      // computed
      hasHearingFields,
      hasInputObjectForHearingFields,
    } = hearingFieldsUtil(props.hearingFieldConfig, props.inputHearingFields)

    const localInputs: Ref<Inputs> = ref({})
    const inputSecondaryLocal: Ref<Inputs> = ref({})

    /**
     * ヒアリング項目があるかどうか
     */
    const hasItems = computed<boolean>(() => {
      return hasHearingFields.value && hasInputObjectForHearingFields.value
    })

    /**
     * ヒアリング項目の情報データとヒアリング項目の入力データの一致性チェック
     */
    const isValidItems = computed<boolean>(() => {
      const sections = props.hearingFieldConfig.sections
      return sections.every(section => {
        // input state にすべてのヒアリング項目が生成済みで、且つ name 属性も正しく定義出来ていること
        return section.items.every(v => {
          return !!props.inputHearingFields[`${v.name}`] && !!props.inputHearingFields[`${v.name}`].name
        })
      })
    })

    watch(() => props.inputHearingFields, () => {
      init()
    }, { deep: true })

    onMounted(() => {
      init()
    })

    /**
     * localInputs に props で渡された input state をバインド
     * 単純に Object assign するとシャローコピーになるため、一旦ローカル変数にディープコピーしたものを assign する
     */
    const init = () => {
      const _ = JSON.parse(JSON.stringify(props.inputHearingFields))

      // 一旦 localInputs を初期化してから親から渡された props の内容を assign
      localInputs.value = {}
      Object.assign(localInputs.value, _)
    }

    /**
     * ヒアリング項目の入力時に、子コンポーネントから emit されたデータを詰め直して親に emit する
     * @param key 入力のあったヒアリング項目の key 名
     * @param value 入力のあったヒアリング項目の value
     */
    const updateHearingFieldValue = (key: string, value: InputValue): void => {
      const _key = Object.keys(localInputs.value).find(v => v === key)
      if (_key) {
        localInputs.value[_key].value = value
        emit('update:hearingFields', localInputs.value)
      }
    }

    /**
     * 「その他」に該当する親選択肢が選択された際の処理
     * 親選択肢が選ばれたら、退避していた追加項目入力情報を親に emit する
     *
     * @param checked 選択のチェック状況
     * @param name 「その他」項目に対する追加質問の name 値
     */
    const onSelectPrimary = (checked: boolean, name: string | undefined, hearingFieldConfigItem: HearingFieldConfigItem) => {
      const _key = name as keyof typeof inputSecondaryLocal.value

      /**
       * 選択に対応した追加設問の入力情報がすでに一時格納領域に確保されている場合はそれを代入
       * 確保されていなかった場合は空の状態で初期化
       */
      const v = inputSecondaryLocal.value[_key]
        ? inputSecondaryLocal.value[_key]
        : hearingFieldConfigItem.secondary
          ? {
              name: `${name}`,
              value: hearingFieldConfigItem.secondary.fieldType === CUSTOM_FIELD_TYPES.CHECKBOX_MULTI || hearingFieldConfigItem.secondary.fieldType === CUSTOM_FIELD_TYPES.SELECT_MULTI
                ? []
                : hearingFieldConfigItem.secondary.fieldType === CUSTOM_FIELD_TYPES.CHECKBOX
                  ? 0
                  : '',
              validationScheme: hearingFieldConfigItem.secondary.validationScheme,
            }
          : undefined

      if (checked && !!name && !!v) {
        emit('select:primary', name, v)
      }
    }

    /**
     * 「その他」該当項目の選択が外され、追加質問が閉じられた（閉じるアコーディオンアニメーションが完全に完了した）際に発火するイベント
     * 追加質問項目の現在の入力状況を退避した上で「その他」項目の選択が外れたイベントを親に emit で通知する
     *
     * @param name 「その他」項目に対する追加質問の name 値
     */
    const onCloseSecondary = (name: string | undefined, hearingFieldConfigItem: HearingFieldConfigItem) => {
      if (!!name && props.inputHearingFields && props.inputHearingFields[`${name}`]) {
        const _key = name as keyof typeof inputSecondaryLocal.value

        // 追加設問の位置時格納用領域に対象の追加設問の領域が確保されていなかった場合は一旦空の領域を作成
        if (!inputSecondaryLocal.value[_key] && hearingFieldConfigItem.secondary) {
          Object.assign(inputSecondaryLocal.value, {
            [`${name}`]: {
              name: `${name}`,
              value: hearingFieldConfigItem.secondary.fieldType === CUSTOM_FIELD_TYPES.CHECKBOX_MULTI || hearingFieldConfigItem.secondary.fieldType === CUSTOM_FIELD_TYPES.SELECT_MULTI
                ? []
                : hearingFieldConfigItem.secondary.fieldType === CUSTOM_FIELD_TYPES.CHECKBOX
                  ? 0
                  : '',
              validationScheme: hearingFieldConfigItem.secondary.validationScheme,
            }
          })
        }

        // 位置時格納用領域に入力状況を逃がした上で親に emit
        bindData(
          inputSecondaryLocal.value[_key],
          props.inputHearingFields[`${name}`])
        emit('close:secondary', name)
      }
    }

    return {
      // data
      localInputs,

      // computed
      hasItems,
      isValidItems,

      // methods
      updateHearingFieldValue,
      onSelectPrimary,
      onCloseSecondary,
    }
  }
})
</script>

<template>
  <div
    v-if="hasItems && isValidItems"
    class="o-list__hearingFieldSection">
    <dl
      v-for="section in hearingFieldConfig.sections"
      :key="section.id"
      class="o-list__hearingFieldSection--unit">
      <dt
        v-if="section.title && isShowSectionTitle"
        class="o-list__hearingFieldSection--term">
        {{ section.title }}
      </dt>

      <dd class="o-list__hearingFieldSection--description">
        <div class="l-rows">
          <div
            v-for="item in section.items"
            :key="item.name"
            class="l-rows--row">
            <InputHearingFieldItem
              v-if="localInputs[`${item.name}`]"
              :model-value="localInputs[`${item.name}`].value"
              :hearing-field-config-item="item"
              :field-id-name-suffix="hearingFieldConfig.nameSuffix.hearingFieldId"
              :user-field-id-name-suffix="hearingFieldConfig.nameSuffix.id"
              :namespace="namespace"
              :name="localInputs[item.name].name"
              :validation-scheme="localInputs[item.name].validationScheme"
              @update:model-value="updateHearingFieldValue(item.name, $event)"
              @select:primary="onSelectPrimary($event, `${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`, item)"
              @close:secondary="onCloseSecondary(`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`, item)">
              <template #secondary>
                <template v-if="item.secondary">
                  <TextareaForm
                    v-if="localInputs[`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`]"
                    :model-value="(localInputs[`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`].value as string)"
                    :header="item.secondary.title"
                    :namespace="namespace"
                    :name="localInputs[`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`].name"
                    :placeholder="item.secondary.placeholder || undefined"
                    :validation-scheme="localInputs[`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`].validationScheme"
                    @update:model-value="updateHearingFieldValue(`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`, $event)" />

                  <!-- 「その他」選択肢が外れた場合は null で送る -->
                  <input
                    v-else
                    class="input"
                    type="hidden"
                    :name="`${item.name}${hearingFieldConfig.nameSuffix.secondaryValue}`">
                </template>
              </template>
            </InputHearingFieldItem>
          <!-- /.l-rows row --></div>
        <!-- /.l-rows --></div>
      </dd>
    </dl>
  <!-- /.o-list__hearingFieldSection --></div>
</template>

<style lang="sass" scoped>
// object
.o-list__hearingFieldSection
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--unit
    margin-top: 2em

    &:first-child
      margin-top: 0

  & &--term
    color: $font-orange
    font-weight: bold

  & &--description
    margin-top: 1em

    &:first-child
      margin-top: 0
</style>
